import { createApi } from "@reduxjs/toolkit/query/react";

import { triggerFileDownload } from "@/shared/helpers/trigger-file-download.helper";
import type { IApiResponse, IPaginationMetadata } from "@/shared/interfaces/api-response.interface";
import type {
  IBoundaryGeometryResponse,
  IBoundaryGeometriesPageResponse,
  IBoundaryGeometriesPageParams,
} from "@/shared/interfaces/boundary-geometry.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import districtsApi from "../districts/districts";
import schoolsApi from "../schools/schools";

import { upsertCustomLayerInListCache } from "./custom-layers-cache.helpers";
import {
  type CustomLayerApiWire,
  type RecoverableCustomLayerApiWire,
  adaptCustomLayerFromApi,
  adaptRecoverableCustomLayerFromApi,
} from "./custom-layers.adapter";
import { fetchCustomLayerBoundariesZip } from "./functions";

import type {
  ICustomLayer,
  ICustomLayerBoundary,
  ICustomLayerBoundaryDetails,
  ICustomLayerBoundaryListResponse,
  ICustomLayerBoundariesOverviewResponse,
  ICustomLayerListResponse,
  ICustomLayerQaDumpResponse,
  ICreateCustomLayerBoundaryRequest,
  IDownloadCustomLayerBoundariesZipSuccessResult,
  IDuplicateCustomLayerRequest,
  IDuplicateLayerFromDistrictBoundariesRequest,
  IDuplicateLayerFromSchoolBoundariesRequest,
  IExportCustomLayerBoundariesZipArg,
  IGetCustomLayersParams,
  IGetSuggestedCustomLayerNamesParams,
  ISuggestedCustomLayerNamesResponse,
  IRecoverableCustomLayerListResponse,
  IResolveGapFillRequest,
  IResolveGapFillResponse,
  IResolveAutoFitRequest,
  IResolveAutoFitResponse,
  ISetCustomLayerScheduledMergeDateRequest,
  IUpdateCustomLayerBoundariesRequest,
  IUpdateCustomLayerBoundaryRequest,
  IUpdateCustomLayerRequest,
  IUpsertCustomLayerBoundariesRequest,
  IUpsertCustomLayerBoundaryRequest,
} from "./custom-layers.interface";

function customLayerBoundariesOverviewTag(layerId: string) {
  return { type: ApiTags.CustomLayers as const, id: `boundaries-overview-${layerId}` };
}

function recoverableCustomLayersTag(districtId: string) {
  return { type: ApiTags.CustomLayers as const, id: `recoverable-${districtId}` };
}

function customLayerBoundaryGeometryScopeTag(layerId: string) {
  return { type: ApiTags.BoundaryGeometry as const, id: `custom-layer:${layerId}` };
}

function customLayerBoundaryGeometryItemTag(layerId: string, boundaryId: string) {
  return { type: ApiTags.BoundaryGeometry as const, id: `custom:${layerId}:${boundaryId}` };
}

function customLayerDistrictBoundaryGeometryItemTag(layerId: string, boundaryId: string) {
  return {
    type: ApiTags.BoundaryGeometry as const,
    id: `custom-district:${layerId}:${boundaryId}`,
  };
}

const GEOMETRY_CACHE_TTL_SECONDS = 300;

const customLayersApi = createApi({
  reducerPath: "customLayersApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.CustomLayers, ApiTags.BoundaryGeometry],
  endpoints: (builder) => ({
    getCustomLayersByDistrict: builder.query<ICustomLayerListResponse, IGetCustomLayersParams>({
      query: ({ districtId, page, limit, status, includeArchived, type, targetType, schoolLevelType }) => ({
        url: `/district/${districtId}/custom-layers`,
        method: "GET",
        params: { page, limit, status, includeArchived, type, targetType, schoolLevelType },
      }),
      providesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<{ items: CustomLayerApiWire[]; pagination: IPaginationMetadata }>) => {
        const data = result.data;
        return {
          items: data.items.map(adaptCustomLayerFromApi),
          pagination: data.pagination,
        };
      },
    }),
    getCustomLayersQaDump: builder.query<ICustomLayerQaDumpResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/district/${districtId}/custom-layers/qa-dump`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<{
        layers: CustomLayerApiWire[];
        auditLogs: ICustomLayerQaDumpResponse["auditLogs"];
      }>) => ({
        layers: result.data.layers.map(adaptCustomLayerFromApi),
        auditLogs: result.data.auditLogs,
      }),
    }),
    getRecoverableCustomLayers: builder.query<
      IRecoverableCustomLayerListResponse,
      { districtId: string }
    >({
      query: ({ districtId }) => ({
        url: `/district/${districtId}/custom-layers/recoverable`,
        method: "GET",
      }),
      providesTags: (_result, _error, { districtId }) => [recoverableCustomLayersTag(districtId)],
      transformResponse: (result: IApiResponse<{ items: RecoverableCustomLayerApiWire[] }>) => ({
        items: result.data.items.map(adaptRecoverableCustomLayerFromApi),
      }),
    }),
    duplicateFromSchoolBoundaries: builder.mutation<
      ICustomLayer,
      { districtId: string; body: IDuplicateLayerFromSchoolBoundariesRequest }
    >({
      query: ({ districtId, body }) => ({
        url: `/district/${districtId}/custom-layers/duplicate-from-school-boundaries`,
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
      async onQueryStarted({ districtId }, api) {
        try {
          const { data: created } = await api.queryFulfilled;
          upsertCustomLayerInListCache(api.dispatch, () => api.getState(), districtId, created);
        } catch (error) {
          console.error(error);
        }
      },
    }),
    duplicateFromDistrictBoundaries: builder.mutation<
      ICustomLayer,
      { districtId: string; body: IDuplicateLayerFromDistrictBoundariesRequest }
    >({
      query: ({ districtId, body }) => ({
        url: `/district/${districtId}/custom-layers/duplicate-from-district-boundaries`,
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
      async onQueryStarted({ districtId }, api) {
        try {
          const { data: created } = await api.queryFulfilled;
          upsertCustomLayerInListCache(api.dispatch, () => api.getState(), districtId, created);
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getSuggestedCustomLayerNames: builder.query<
      ISuggestedCustomLayerNamesResponse,
      IGetSuggestedCustomLayerNamesParams
    >({
      query: ({ districtId, schoolLevelType, name, count }) => ({
        url: `/district/${districtId}/custom-layers/suggested-names`,
        method: "GET",
        params: { schoolLevelType, name, count },
      }),
      transformResponse: (result: IApiResponse<ISuggestedCustomLayerNamesResponse>) =>
        result.data,
    }),
    duplicateCustomLayer: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string; body: IDuplicateCustomLayerRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/duplicate`,
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
      async onQueryStarted({ districtId }, api) {
        try {
          const { data: created } = await api.queryFulfilled;
          upsertCustomLayerInListCache(api.dispatch, () => api.getState(), districtId, created);
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateCustomLayer: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string; body: IUpdateCustomLayerRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
    }),
    setCustomLayerScheduledMergeDate: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string; body: ISetCustomLayerScheduledMergeDateRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/scheduled-merge-date`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
          dispatch(schoolsApi.util.invalidateTags([ApiTags.Schools]));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    archiveCustomLayer: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { districtId }) => [
        ApiTags.CustomLayers,
        recoverableCustomLayersTag(districtId),
      ],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
    }),
    unarchiveCustomLayer: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { districtId }) => [
        ApiTags.CustomLayers,
        recoverableCustomLayersTag(districtId),
      ],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
    }),
    makeDraftCustomLayer: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/make-draft`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { districtId }) => [
        ApiTags.CustomLayers,
        recoverableCustomLayersTag(districtId),
      ],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
    }),
    setCustomLayerPending: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/set-pending`,
        method: "POST",
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
    }),
    setCustomLayerApplied: builder.mutation<
      ICustomLayer,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/set-applied`,
        method: "POST",
      }),
      invalidatesTags: [ApiTags.CustomLayers],
      transformResponse: (result: IApiResponse<CustomLayerApiWire>) => adaptCustomLayerFromApi(result.data),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
          dispatch(schoolsApi.util.invalidateTags([ApiTags.Schools]));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getCustomLayerBoundaryDetails: builder.query<
      ICustomLayerBoundaryDetails,
      { districtId: string; layerId: string; boundaryId: string }
    >({
      query: ({ districtId, layerId, boundaryId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/${boundaryId}`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<ICustomLayerBoundaryDetails>) =>
        result.data,
    }),
    getCustomLayerBoundaryGeometry: builder.query<
      IBoundaryGeometryResponse,
      { districtId: string; layerId: string; boundaryId: string }
    >({
      query: ({ districtId, layerId, boundaryId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/${boundaryId}/geometry`,
        method: "GET",
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { layerId, boundaryId }) => [
        customLayerBoundaryGeometryItemTag(layerId, boundaryId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometryResponse>) => result.data,
    }),
    getCustomLayerDistrictBoundaryGeometry: builder.query<
      IBoundaryGeometryResponse,
      { districtId: string; layerId: string; boundaryId: string }
    >({
      query: ({ districtId, layerId, boundaryId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/district-boundaries/${boundaryId}/geometry`,
        method: "GET",
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { layerId, boundaryId }) => [
        customLayerDistrictBoundaryGeometryItemTag(layerId, boundaryId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometryResponse>) => result.data,
    }),
    getCustomLayerBoundaryGeometries: builder.query<
      IBoundaryGeometriesPageResponse,
      { districtId: string; layerId: string } & IBoundaryGeometriesPageParams
    >({
      query: ({ districtId, layerId, cursor, limit }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/geometries`,
        method: "GET",
        params: {
          ...(cursor != null ? { cursor } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { layerId }) => [customLayerBoundaryGeometryScopeTag(layerId)],
      transformResponse: (result: IApiResponse<IBoundaryGeometriesPageResponse>) => result.data,
    }),
    getCustomLayerDistrictBoundaryGeometries: builder.query<
      IBoundaryGeometriesPageResponse,
      { districtId: string; layerId: string } & IBoundaryGeometriesPageParams
    >({
      query: ({ districtId, layerId, cursor, limit }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/district-boundaries/geometries`,
        method: "GET",
        params: {
          ...(cursor != null ? { cursor } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { layerId }) => [customLayerBoundaryGeometryScopeTag(layerId)],
      transformResponse: (result: IApiResponse<IBoundaryGeometriesPageResponse>) => result.data,
    }),
    getCustomLayerBoundariesList: builder.query<
      ICustomLayerBoundariesOverviewResponse,
      { districtId: string; layerId: string }
    >({
      query: ({ districtId, layerId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries`,
        method: "GET",
      }),
      providesTags: (_result, _error, { layerId }) => [customLayerBoundariesOverviewTag(layerId)],
      transformResponse: (result: IApiResponse<ICustomLayerBoundariesOverviewResponse>) =>
        result.data,
    }),
    createCustomLayerBoundary: builder.mutation<
      ICustomLayerBoundary,
      { districtId: string; layerId: string; body: ICreateCustomLayerBoundaryRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundary>) => result.data,
    }),
    upsertCustomLayerBoundary: builder.mutation<
      ICustomLayerBoundary,
      { districtId: string; layerId: string; body: IUpsertCustomLayerBoundaryRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundary>) => result.data,
    }),
    updateCustomLayerBoundary: builder.mutation<
      ICustomLayerBoundary,
      {
        districtId: string;
        layerId: string;
        boundaryId: string;
        body: IUpdateCustomLayerBoundaryRequest;
      }
    >({
      query: ({ districtId, layerId, boundaryId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/${boundaryId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundary>) => result.data,
    }),
    deleteCustomLayerBoundary: builder.mutation<
      ICustomLayerBoundary,
      { districtId: string; layerId: string; boundaryId: string }
    >({
      query: ({ districtId, layerId, boundaryId }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/${boundaryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundary>) => result.data,
    }),
    upsertCustomLayerBoundaries: builder.mutation<
      ICustomLayerBoundaryListResponse,
      { districtId: string; layerId: string; body: IUpsertCustomLayerBoundariesRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/bulk`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundaryListResponse>) =>
        result.data,
    }),
    updateCustomLayerBoundaries: builder.mutation<
      ICustomLayerBoundaryListResponse,
      { districtId: string; layerId: string; body: IUpdateCustomLayerBoundariesRequest }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/bulk`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { layerId }) => [
        ApiTags.CustomLayers,
        customLayerBoundariesOverviewTag(layerId),
        customLayerBoundaryGeometryScopeTag(layerId),
      ],
      transformResponse: (result: IApiResponse<ICustomLayerBoundaryListResponse>) =>
        result.data,
    }),
    downloadCustomLayerBoundariesZip: builder.mutation<
      IDownloadCustomLayerBoundariesZipSuccessResult,
      IExportCustomLayerBoundariesZipArg
    >({
      queryFn: async (arg) => {
        const result = await fetchCustomLayerBoundariesZip(arg);

        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }

        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: {
            success: true as const,
            filename: result.data!.filename,
          },
        };
      },
    }),
    resolveCustomLayerGapFill: builder.mutation<
      IResolveGapFillResponse,
      {
        districtId: string;
        layerId: string;
        body: IResolveGapFillRequest;
      }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/gap-fill/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveGapFillResponse>) => result.data,
    }),
    resolveCustomLayerAutoFit: builder.mutation<
      IResolveAutoFitResponse,
      {
        districtId: string;
        layerId: string;
        body: IResolveAutoFitRequest;
      }
    >({
      query: ({ districtId, layerId, body }) => ({
        url: `/district/${districtId}/custom-layers/${layerId}/boundaries/auto-fit/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveAutoFitResponse>) => result.data,
    }),
  }),
});

export const {
  useGetCustomLayersByDistrictQuery,
  useLazyGetCustomLayersQaDumpQuery,
  useGetRecoverableCustomLayersQuery,
  useLazyGetRecoverableCustomLayersQuery,
  useGetSuggestedCustomLayerNamesQuery,
  useLazyGetSuggestedCustomLayerNamesQuery,
  useLazyGetCustomLayerBoundaryDetailsQuery,
  useGetCustomLayerBoundaryDetailsQuery,
  useLazyGetCustomLayerBoundaryGeometryQuery,
  useGetCustomLayerBoundaryGeometryQuery,
  useLazyGetCustomLayerDistrictBoundaryGeometryQuery,
  useGetCustomLayerDistrictBoundaryGeometryQuery,
  useLazyGetCustomLayerBoundaryGeometriesQuery,
  useGetCustomLayerBoundaryGeometriesQuery,
  useLazyGetCustomLayerDistrictBoundaryGeometriesQuery,
  useGetCustomLayerDistrictBoundaryGeometriesQuery,
  useGetCustomLayerBoundariesListQuery,
  useLazyGetCustomLayerBoundariesListQuery,
  useDuplicateFromSchoolBoundariesMutation,
  useDuplicateFromDistrictBoundariesMutation,
  useDuplicateCustomLayerMutation,
  useUpdateCustomLayerMutation,
  useSetCustomLayerScheduledMergeDateMutation,
  useArchiveCustomLayerMutation,
  useUnarchiveCustomLayerMutation,
  useMakeDraftCustomLayerMutation,
  useSetCustomLayerPendingMutation,
  useSetCustomLayerAppliedMutation,
  useCreateCustomLayerBoundaryMutation,
  useUpsertCustomLayerBoundaryMutation,
  useUpdateCustomLayerBoundaryMutation,
  useDeleteCustomLayerBoundaryMutation,
  useUpsertCustomLayerBoundariesMutation,
  useUpdateCustomLayerBoundariesMutation,
  useDownloadCustomLayerBoundariesZipMutation,
  useResolveCustomLayerGapFillMutation,
  useResolveCustomLayerAutoFitMutation,
} = customLayersApi;

export default customLayersApi;
